import { Slice } from '@tiptap/pm/model';
import type { StarterKitOptions } from '@tiptap/starter-kit';
import type React from 'react';

/** Uploads an image and resolves with its permanent, publicly readable URL. */
export type UploadImageFn = (
	file: File,
	onProgress: (percent: number) => void
) => Promise<string>;

export interface MentionProps {
	items: MentionItem[];
	emptyPlaceholderText?: string;
	showAddNewMentionButton?: boolean;
	addOptionText?: string;
	onAddNewMentionClick?: () => void;
	contentOnResultNotFound?: (query: string) => React.JSX.Element | null;
	additionalChildren?: React.JSX.Element;
}

export interface VariableItem {
	id: string;
	token: string;
	label: string;
	prefix?: string;
	description?: string;
	sampleValue?: string;
}

export interface VariableProps {
	items: VariableItem[];
	onSelect?: (item: VariableItem) => void;
	getTooltip?: (item: VariableItem) => string;
	triggerChar?: string;
	/** When false, the variable pill renders the label without curly-brace brackets. Defaults to true. */
	showBrackets?: boolean;
	/** Render variables as an inline `@mention` (blue text, no background, `@` prefix)
	 *  instead of a filled pill. Used by the AI agent's form mentions. Defaults to false. */
	mentionStyle?: boolean;
	/**
	 * Optional async suggestion source. When provided, the dropdown debounces the
	 * typed query into this and shows a loading state while it resolves. Results
	 * render alongside (below) any static `items`. Leave undefined for the classic
	 * in-memory-only behaviour.
	 */
	searchItems?: (query: string) => Promise<VariableItem[]>;
	/** Min query length before `searchItems` fires. 0 = fire on the bare trigger char. Default 0. */
	searchMinChars?: number;
	/** Debounce before `searchItems` fires, in ms. Default 200. */
	searchDebounceMs?: number;
	/** Copy shown when a resolved search returns nothing. */
	searchEmptyText?: string;
	/** Section label pinned above the async results (e.g. "Forms"), so the list's scope is clear. */
	searchHeader?: string;
	/** Row pinned below the results, always shown and keyboard-selectable; the popup hides before `onSelect` runs. */
	footerAction?: { label: string; icon?: React.ReactNode; onSelect: () => void };
	/** Prefix rendered for mention-style variable pills. Defaults to `@`. */
	mentionPrefix?: string;
}

export interface IRichTextEditorProps {
	description: string;
	onChangeDescription?: (description: string) => void;
	editMode?: boolean;
	className?: string;
	onFocus?: () => void;
	onBlur?: () => void;
	isFocusedField?: boolean;
	debounceDuration?: number;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	allowedExtensions?: RTE_EXTENSIONS[];
	placeholder?: string;
	contentClassName?: string;
	hideToolbar?: boolean;
	stopOnChangeDebounce?: boolean;
	mentionProps?: MentionProps;
	// New: when true, hides the animated focus underline at the bottom
	hideFocusUnderline?: boolean;
	// Variable tokens configuration
	variableProps?: VariableProps;
	/** Additional independently configured variable triggers. */
	additionalVariableProps?: VariableProps[];
	/** Enforce a single line: block Enter (except to pick a variable) and flatten pasted
	 *  newlines to spaces. The editor still wraps long content. */
	singleLine?: boolean;
	onPaste?: (event: ClipboardEvent, slice: Slice) => void;
	/**
	 * Toolbar button keys to hide (e.g., ['link', 'heading']).
	 * The extension still loads (for features like link auto-detect), but the button won't show.
	 */
	hiddenToolbarOptions?: string[];
	/** Required for RTE_EXTENSIONS.IMAGE to do anything. */
	uploadImage?: UploadImageFn;
	/** Mount-time StarterKit overrides for call sites with a deliberately narrow schema. */
	starterKitOptions?: Partial<StarterKitOptions>;
	/** On blur, trim content until the serialized HTML (markup included) fits this many characters. */
	maxHtmlLengthOnBlur?: number;
	onCharacterLimitExceeded?: (limit: number) => void;
}

export enum RTE_EXTENSIONS {
	STARTER_KIT = 'starter-kit',
	SLASH_COMMAND = 'slash-command',
	LINK = 'link',
	UNDERLINE = 'underline',
	ORDERED_LIST = 'ordered-list',
	HEADING = 'heading',
	PLACEHOLDER = 'placeholder',
	BULLET_LIST = 'bullet-list',
	LIST_ITEM = 'list-item',
	LIST_KEYMAP = 'list-keymap',
	MENTIONS = 'mentions',
	TYPOGRAPHY = 'typography',
	VARIABLE = 'variable',
	IMAGE = 'image',
	YOUTUBE = 'youtube',
	DROP_CURSOR = 'drop-cursor',
}

export interface MentionItem {
	id: string;
	name: string;
	email?: string;
	avatarUrl?: string;
}
